import React, { useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useNavigationHistory } from '../../contexts/NavigationHistoryContext';
import { getDefaultRouteLabel } from '../../config/routeLabels';

const ChevronRight = () => (
  <svg className="w-3.5 h-3.5 text-ink-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.25}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
  </svg>
);

/** Derive parent breadcrumbs from URL path segments using ROUTE_LABELS */
function buildStructuralBreadcrumbs(pathname: string): { path: string; label: string }[] {
  const segments = pathname.replace(/^\//, '').split('/');
  const parents: { path: string; label: string }[] = [];

  for (let i = 1; i < segments.length; i++) {
    const parentPath = '/' + segments.slice(0, i).join('/');
    const label = getDefaultRouteLabel(parentPath);
    if (label) {
      parents.push({ path: parentPath, label });
    }
  }

  return parents;
}

const NavigationBreadcrumbs: React.FC = () => {
  const { history, current } = useNavigationHistory();
  const { pathname } = useLocation();

  const structuralBreadcrumbs = useMemo(
    () => (history.length === 0 ? buildStructuralBreadcrumbs(pathname) : []),
    [history.length, pathname]
  );

  const displayHistory = history.length > 0 ? history : structuralBreadcrumbs;

  if (displayHistory.length === 0 && !current) return null;
  if (displayHistory.length === 0) return null;

  return (
    <div className="hidden md:block border-t border-border">
      <div className="container-content">
        <nav className="flex items-center gap-1.5 py-1.5 text-sm" aria-label="Navigation history">
          {displayHistory.map((entry, i) => (
            <React.Fragment key={`${entry.path}-${i}`}>
              <Link
                to={entry.path}
                className="text-muted-foreground hover:text-primary-brand truncate max-w-[200px]"
                title={entry.label}
              >
                {entry.label}
              </Link>
              <ChevronRight />
            </React.Fragment>
          ))}
          {current && (
            <span className="text-foreground font-medium truncate max-w-[200px]" title={current.label}>
              {current.label}
            </span>
          )}
        </nav>
      </div>
    </div>
  );
};

export default NavigationBreadcrumbs;
